import { useState, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { requestAccountDeletion } from './account-deletion-service'

export function AccountDeletionPanel() {
  const { user, signOut } = useAuth()
  const [email, setEmail] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [submittedMessage, setSubmittedMessage] = useState<string | null>(null)

  const requestDeletion = useMutation({
    mutationFn: requestAccountDeletion,
    onSuccess: async (result) => {
      setSubmittedMessage(result.requiresOwnershipTransfer
        ? `${result.message} You own one or more organizations; PropertyReady will verify ownership transfer before final deletion.`
        : result.message)
      await signOut()
    },
  })

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    requestDeletion.mutate({ email, confirmation })
  }

  if (submittedMessage) return <section className="panel">
    <p className="eyebrow">Account deletion requested</p>
    <h2>Your request was accepted</h2>
    <p>{submittedMessage}</p>
  </section>

  return <section className="panel" aria-labelledby="delete-account-title">
    <p className="eyebrow">Account & privacy</p>
    <h2 id="delete-account-title">Delete my account</h2>
    <p>Permanently request deletion of your PropertyReady account and associated personal information.</p>
    <p className="form-note">Marketplace transactions, payment records, tax records, disputes, fraud-prevention records, and legal-hold information may be retained when required. If you own an organization, ownership must be transferred or resolved before deletion is completed.</p>
    <form className="security-form" onSubmit={submit}>
      <label>Confirm your account email
        <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder={user?.email ?? 'you@example.com'} required autoComplete="email" />
      </label>
      <label>Type DELETE to confirm
        <input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required autoCapitalize="characters" autoComplete="off" />
      </label>
      {requestDeletion.error && <div className="form-error" role="alert">{requestDeletion.error.message}</div>}
      <button className="danger-button" disabled={requestDeletion.isPending || confirmation !== 'DELETE' || !email.trim()}>
        {requestDeletion.isPending ? 'Submitting deletion request…' : 'Request permanent account deletion'}
      </button>
    </form>
  </section>
}
